import { motion, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Flame, Ticket, TrendingUp } from "lucide-react";
import { API_BASE as API } from "../../lib/api";

// Counts up to `value` once `start` becomes true.
function CountUp({ value, start }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!start) return;
    const controls = animate(0, value, {
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [start, value]);
  return <span>{display.toLocaleString("es-CO")}</span>;
}

export default function FichaProgress() {
  const [stats, setStats] = useState(null);
  const [reveal, setReveal] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/stats`)
      .then((res) => active && setStats(res.data))
      .catch(() => active && setStats({ total: 3000, sold: 1875 }));
    return () => {
      active = false;
    };
  }, []);

  // Trigger the count-up + bar fill once stats has loaded. Reliable and
  // guarantees the final state is always rendered correctly.
  useEffect(() => {
    if (!stats) return;
    const id = requestAnimationFrame(() => setReveal(true));
    return () => cancelAnimationFrame(id);
  }, [stats]);

  if (!stats) {
    return (
      <section className="relative py-20 bg-[#051A0F]" data-testid="progress-loading" />
    );
  }

  const pct = Math.min(100, Math.round((stats.sold / stats.total) * 100));
  const remaining = Math.max(0, stats.total - stats.sold);
  const almostGone = pct >= 70;

  return (
    <section
      ref={sectionRef}
      className="relative py-24 md:py-28 overflow-hidden"
      data-testid="progress-section"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#051A0F] via-[#0A2818] to-[#051A0F]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-[#F0B91B]/10 blur-[130px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E63946]/30 bg-[#E63946]/10 mb-6">
            <Flame className="w-4 h-4 text-[#E63946]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#E63946] font-semibold font-outfit">
              {almostGone ? "Últimas fichas" : "Fichas disponibles"}
            </span>
          </div>
          <h2 className="font-bebas text-5xl md:text-7xl text-[#FDFBF7] tracking-wider">
            {almostGone ? (
              <>
                Se están <span className="gold-shimmer">agotando</span>
              </>
            ) : (
              <>
                No te quedes <span className="gold-shimmer">por fuera</span>
              </>
            )}
          </h2>
          <p className="mt-4 text-lg text-[#FDFBF7]/70 max-w-2xl mx-auto font-outfit">
            Cada ficha vendida es una oportunidad menos. Asegura la tuya antes del sorteo.
          </p>
        </motion.div>

        {/* Progress card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-3xl border border-[#F0B91B]/25 bg-[#0A2818]/70 backdrop-blur-md p-8 md:p-10 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.7)]"
          data-testid="progress-card"
        >
          <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-[#F0B91B] mb-1">
                <Ticket className="w-4 h-4" />
                <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-outfit font-semibold">
                  Fichas vendidas
                </span>
              </div>
              <div className="font-bebas text-5xl md:text-6xl text-[#FDFBF7] tracking-wider leading-none" data-testid="progress-sold">
                <CountUp value={stats.sold} start={reveal} />
                <span className="text-[#FDFBF7]/40"> / {stats.total.toLocaleString("es-CO")}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end text-[#7FD1AE] mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-outfit font-semibold">
                  Progreso
                </span>
              </div>
              <div className="font-bebas text-5xl md:text-6xl gold-shimmer tracking-wider leading-none">
                <CountUp value={pct} start={reveal} />%
              </div>
            </div>
          </div>

          {/* Bar */}
          <div className="relative h-6 rounded-full bg-[#051A0F] border border-[#F0B91B]/20 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#E5A200] via-[#F0B91B] to-[#FFD700] shadow-[0_0_20px_rgba(240,185,27,0.5)] transition-[width] duration-[1500ms] ease-out"
              style={{ width: reveal ? `${pct}%` : "0%", transitionDelay: "200ms" }}
              data-testid="progress-bar-fill"
            >
              <div className="absolute inset-0 opacity-40 bg-[linear-gradient(45deg,rgba(255,255,255,0.35)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.35)_50%,rgba(255,255,255,0.35)_75%,transparent_75%)] bg-[length:20px_20px]" />
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E63946]/15 border border-[#E63946]/30"
              data-testid="progress-remaining"
            >
              <Flame className="w-4 h-4 text-[#E63946]" />
              <span className="font-outfit text-sm text-[#FDFBF7]/90">
                Solo quedan{" "}
                <strong className="text-[#FDFBF7] font-bebas text-lg tracking-wider">
                  {remaining.toLocaleString("es-CO")}
                </strong>{" "}
                fichas
              </span>
            </div>

            <a
              href="#elegir-ficha"
              className="inline-flex items-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] px-6 py-3 rounded-full font-bebas text-xl tracking-wider shadow-[0_10px_30px_-10px_rgba(240,185,27,0.7)] hover:-translate-y-0.5 transition-all"
              data-testid="progress-cta"
            >
              Reservar mi Ficha
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
